import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import ExportDialog from "./ExportDialog";
import type { ExportConfig } from "./types";

export type ExportResult = "exported" | "cancelled";

type ExportContextValue = {
  /** Opens the app-wide export dialog; resolves when the user downloads or closes it. */
  open: (config: ExportConfig) => Promise<ExportResult>;
};

const ExportContext = createContext<ExportContextValue | null>(null);

type OpenState = {
  config: ExportConfig;
  resolve: (result: ExportResult) => void;
};

export const ExportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<OpenState | null>(null);
  const stateRef = useRef<OpenState | null>(null);
  stateRef.current = state;

  const open = useCallback((config: ExportConfig) => {
    stateRef.current?.resolve("cancelled");
    return new Promise<ExportResult>((resolve) => {
      setState({ config, resolve });
    });
  }, []);

  const finish = useCallback((result: ExportResult) => {
    stateRef.current?.resolve(result);
    setState(null);
  }, []);

  const value = useMemo(() => ({ open }), [open]);

  return (
    <ExportContext.Provider value={value}>
      {children}
      {state ? (
        <ExportDialog
          key={`${state.config.reportType}:${JSON.stringify(state.config.params)}:${JSON.stringify(state.config.initialFilters ?? {})}:${state.config.initialGranularity ?? ""}`}
          config={state.config}
          onExported={() => finish("exported")}
          onClose={() => finish("cancelled")}
        />
      ) : null}
    </ExportContext.Provider>
  );
};

export function useExport(): ExportContextValue {
  const ctx = useContext(ExportContext);
  if (!ctx) throw new Error("useExport must be used inside <ExportProvider>");
  return ctx;
}
